import { Flex } from "@redotech/arbiter/flex";
import { RedoIncrementDecrement } from "@redotech/arbiter/increment-decrement/redo-increment-decrement";
import { TextRedoListItem } from "@redotech/arbiter/list/redo-list";
import { RedoRadioGroup } from "@redotech/arbiter/radio/redo-radio-group";
import { RedoSingleSelectDropdownInput } from "@redotech/arbiter/select-dropdown/redo-single-select-dropdown-input";
import { Text } from "@redotech/arbiter/text";
import { RedoToggle } from "@redotech/arbiter/toggle/redo-toggle";
import { getCurrencySymbol } from "@redotech/money/currencies";
import { Currency } from "@redotech/money/iso4217/currencies";
import { useRequiredContext } from "@redotech/react-util/context";
import { PaymentProviderType } from "@redotech/redo-billing-common/billing-engine-types";
import {
  BillingPeriod,
  billingPeriodToFriendlyUpper,
} from "@redotech/redo-billing-common/service-invoice";
import { SUBSCRIPTION_FEE_CURRENCIES } from "@redotech/redo-billing-common/team-billing";
import { teamGetPaymentMethod } from "@redotech/redo-team-common/team";
import { DateInput } from "@redotech/redo-web/date-picker";
import { LabeledInput } from "@redotech/redo-web/labeled-input";
import { getDateStartOfDayInTimezone } from "@redotech/util/date";
import { memo, useEffect, useMemo } from "react";
import { TeamContext } from "../../../../team";
import { SubscriptionBillingForm } from "./subscription-billing-form";
import * as styles from "./subscription-billing.module.css";

const feeCurrencyOptions: TextRedoListItem<Currency>[] =
  SUBSCRIPTION_FEE_CURRENCIES.map((currency) => ({
    type: "text",
    id: currency,
    text: currency,
    value: currency,
  }));

export const SubscriptionBillingCardV2 = memo(
  function SubscriptionBillingCardV2({
    input,
  }: {
    input: SubscriptionBillingForm;
  }) {
    const team = useRequiredContext(TeamContext);
    const teamTimezone = team._shopify?.iana_timezone || "America/Los_Angeles";
    const now = getDateStartOfDayInTimezone(new Date(), teamTimezone);
    const {
      enabled,
      startDate,
      isStartDateInFuture,
      renewOnPeriodEnd,
      periodFeeInDollars,
      currency,
      period,
      paymentProvider,
    } = input.inputs;

    const subscriptionAlreadyStarted = useMemo(() => {
      const alreadyEnabled = !enabled.changed && enabled.value;

      if (!alreadyEnabled) {
        return false;
      }

      const startDateIsTodayOrBefore =
        !!startDate.value &&
        getDateStartOfDayInTimezone(startDate.value, teamTimezone).getTime() <=
          now.getTime();

      if (startDateIsTodayOrBefore) {
        return true;
      }

      return false;
    }, [teamTimezone, startDate, enabled, now]);

    const shouldDisableToggle = useMemo(() => {
      const startDateIsInPast =
        !!startDate.value &&
        getDateStartOfDayInTimezone(startDate.value, teamTimezone).getTime() <
          now.getTime();

      return enabled.value && startDateIsInPast;
    }, [enabled, startDate, now, teamTimezone]);

    useEffect(() => {
      const startDateIsNew = startDate.changed;
      if (!startDateIsNew || !startDate.value) {
        if (!isStartDateInFuture.value) {
          isStartDateInFuture.setValue(true);
        }
        return;
      }

      const startDateIsInThePast =
        getDateStartOfDayInTimezone(startDate.value, teamTimezone).getTime() <
        getDateStartOfDayInTimezone(now, teamTimezone).getTime();

      if (isStartDateInFuture.value !== !startDateIsInThePast) {
        isStartDateInFuture.setValue(!startDateIsInThePast);
      }
    }, [startDate, isStartDateInFuture, now, teamTimezone]);

    return (
      <>
        <RedoToggle
          description="Enable subscription billing. Once subscription has started, disable by turning off auto renewal."
          disabled={shouldDisableToggle}
          label="Enabled"
          setValue={(val) => {
            enabled.setValue(val);
            renewOnPeriodEnd.setValue(val);
            if (!val) {
              startDate.setValue(null);
            }
          }}
          size="sm"
          value={enabled.value}
        />
        <RedoToggle
          description="Automatically renew subscription at the end of each billing period"
          disabled={!enabled.value}
          label="Auto renewal"
          setValue={renewOnPeriodEnd.setValue}
          size="sm"
          value={renewOnPeriodEnd.value}
        />
        <LabeledInput
          description="How much to charge each period"
          label="Billing period fee"
        >
          <Flex align="center" dir="row" gap="sm" w="full">
            <RedoSingleSelectDropdownInput<Currency>
              ariaLabel="Fee currency"
              className={styles.feeCurrency}
              disabled={!enabled.value}
              fitToAnchor={false}
              flexGrow={false}
              options={feeCurrencyOptions}
              optionSelected={({ item }) => currency.setValue(item.value)}
              selectedItem={feeCurrencyOptions.find(
                (option) => option.value === currency.value,
              )}
              size="sm"
            />
            <RedoIncrementDecrement
              disabled={!enabled.value}
              fullWidth
              min={0}
              prefix={getCurrencySymbol(currency.value)}
              setValue={periodFeeInDollars.setValue}
              step={10}
              value={periodFeeInDollars.value}
            />
          </Flex>
        </LabeledInput>
        <LabeledInput
          description="The date when subscription billing will begin"
          label="Start date"
        >
          <DateInput
            disabled={!enabled.value || subscriptionAlreadyStarted}
            value={
              startDate.value
                ? (() => {
                    const storedDate = startDate.value;
                    const zonedDateTime =
                      Temporal.Instant.fromEpochMilliseconds(
                        storedDate.getTime(),
                      ).toZonedDateTimeISO(teamTimezone);
                    return new Date(
                      zonedDateTime.year,
                      zonedDateTime.month - 1,
                      zonedDateTime.day,
                    );
                  })()
                : null
            }
            valueChange={(date) => {
              if (!date) {
                startDate.setValue(date);
              } else {
                startDate.setValue(
                  getDateStartOfDayInTimezone(date, teamTimezone),
                );
              }
            }}
          />
        </LabeledInput>
        <RedoRadioGroup
          description="How often to charge for the subscription"
          disabled={!enabled.value}
          label="Billing period"
          options={Object.values(BillingPeriod).map((periodValue) => ({
            id: periodValue,
            label: billingPeriodToFriendlyUpper[periodValue],
          }))}
          setValue={period.setValue}
          value={period.value}
        />
        <RedoRadioGroup
          description="Select which payment provider to use for billing"
          disabled={!enabled.value}
          label="Payment provider"
          options={Object.values(PaymentProviderType).map((provider) => ({
            id: provider,
            label:
              provider === PaymentProviderType.SHOPIFY
                ? "Shopify"
                : provider === PaymentProviderType.STRIPE
                  ? "Stripe"
                  : provider,
          }))}
          setValue={paymentProvider.setValue}
          value={paymentProvider.value}
        />
        {paymentProvider.value === PaymentProviderType.STRIPE &&
          !teamGetPaymentMethod(team.stripe ?? undefined) && (
            <Text fontSize="xs" textColor="warning">
              Team has no stripe payment method configured. Billing will fail
              until the team adds a payment method.
            </Text>
          )}
      </>
    );
  },
);
